/*
 * Mery – Cookie-Banner (<mery-consent>) für Shopifys Customer Privacy API.
 * - Zeigt sich nur, wenn Shopify eine Entscheidung verlangt (shouldShowBanner) und Shopifys eigener
 *   Banner nicht aktiv ist (window.privacyBanner). Test: URL mit #cookie-test öffnen.
 * - Speichert die Auswahl über setTrackingConsent – Shopify-Analytics & Pixel halten sich daran.
 * - Wieder öffnen: Footer „Cookie-Einstellungen“ (Event „mery:consent-open“).
 */
class MeryConsent extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-consent-main]');
    this.form = this.querySelector('[data-consent-form]');
    this.moreButton = this.querySelector('[data-consent-settings]');

    this.addEventListener('click', (event) => {
      const action = event.target.closest('[data-consent]')?.dataset.consent;
      if (action === 'all') this.save({ analytics: true, marketing: true, preferences: true });
      if (action === 'necessary') this.save({ analytics: false, marketing: false, preferences: false });
      if (action === 'save') {
        const data = new FormData(this.form);
        this.save({ analytics: data.has('analytics'), marketing: data.has('marketing'), preferences: data.has('preferences') });
      }
      if (event.target.closest('[data-consent-settings]')) this.showSettings();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.opened) this.close();
    });
    document.addEventListener('mery:consent-open', () => this.open(true));

    const forced = window.location.hash === '#cookie-test';
    this.withApi((privacy) => {
      if (window.privacyBanner && !forced) return; // Shopifys Banner ist aktiv – nicht doppelt zeigen
      const consent = privacy.currentVisitorConsent?.() || {};
      const decided = Object.values(consent).some((value) => value === 'yes' || value === 'no');
      if (forced || (privacy.shouldShowBanner() && !decided)) this.open(false, forced);
    });
  }

  withApi(callback) {
    const run = () => {
      if (window.Shopify?.customerPrivacy) return callback(window.Shopify.customerPrivacy);
      if (!window.Shopify?.loadFeatures) return;
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error && window.Shopify.customerPrivacy) callback(window.Shopify.customerPrivacy);
      });
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  open(withSettings, forced = false) {
    if (!forced && !withSettings && window.privacyBanner) return;
    if (!forced && withSettings && window.privacyBanner) {
      window.privacyBanner.showPreferences?.();
      return;
    }
    this.hidden = false;
    this.opened = true;
    void this.offsetHeight; // Reflow erzwingen, damit die Einblendung startet
    this.classList.add('is-visible');
    this.syncForm();
    if (withSettings) this.showSettings();
    else this.querySelector('[data-consent="all"]')?.focus({ preventScroll: true });
  }

  showSettings() {
    this.form.hidden = false;
    this.main.hidden = true;
    this.moreButton?.setAttribute('aria-expanded', 'true');
    this.form.querySelector('input:not([disabled])')?.focus();
  }

  syncForm() {
    const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.() || {};
    for (const key of ['analytics', 'marketing', 'preferences']) {
      const input = this.form.querySelector(`[name="${key}"]`);
      if (input) input.checked = consent[key] === 'yes';
    }
  }

  save(choice) {
    this.withApi((privacy) => {
      privacy.setTrackingConsent({ ...choice, sale_of_data: choice.marketing }, () => this.close());
    });
    // Falls die API nicht erreichbar ist, trotzdem schließen
    setTimeout(() => this.close(), 1500);
  }

  close() {
    if (!this.opened) return;
    this.opened = false;
    this.classList.remove('is-visible');
    setTimeout(() => {
      this.hidden = true;
      this.main.hidden = false;
      this.form.hidden = true;
    }, 300);
  }
}

if (!customElements.get('mery-consent')) customElements.define('mery-consent', MeryConsent);
